import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Modal,
  Alert,
  ActivityIndicator,
  ScrollView,
  Switch,
} from 'react-native';
import { useAuth } from '../Utils/AuthProvider';
import { createAnnouncement, updateAnnouncement } from '../services/postService';
import { canPostAnnouncement } from '../services/communityService';
import { theme } from '../constants/theme';
import { launchImageLibrary } from 'react-native-image-picker';
import { PermissionManager } from '../Utils/permissions';
import { uploadFile } from '../services/imageService';
import { generateVideoThumbnail } from '../Utils/videoThumbnails';
import { Image } from 'react-native';
import { validateFileSize, showFileSizeError, showFileSizeInfo } from '../Utils/fileValidation';
import VideoPlayer from 'react-native-video';
import ImageIcon from '../assets/icons/Image';
import VideoIcon from '../assets/icons/Video';
import FullScreenMedia from './FullScreenMedia';

interface AnnouncementPostProps {
  communityId: string;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editPost?: any;
  isEditMode?: boolean;
}

interface FileAsset {
  uri: string;
  type: string;
  fileName?: string;
}

const AnnouncementPost: React.FC<AnnouncementPostProps> = ({
  communityId,
  visible,
  onClose,
  onSuccess,
  editPost,
  isEditMode = false,
}) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [media, setMedia] = useState<FileAsset | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canPost, setCanPost] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoPaused, setVideoPaused] = useState(true);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [fullScreenMediaType, setFullScreenMediaType] = useState<'image' | 'video'>('image');

  useEffect(() => {
    if (visible && user && communityId) {
      // Reset canPost when modal opens
      setCanPost(false);
      setCheckingPermissions(true);
      checkPermissions();
    } else if (!visible) {
      // Reset state when modal closes
      setCanPost(false);
      setCheckingPermissions(false);
    }
    
    // Initialize form with existing data when editing
    if (visible && isEditMode && editPost) {
      // Parse title and content from body (format: **title**\n\ncontent)
      const bodyText = editPost.body || '';
      const titleMatch = bodyText.match(/^\*\*(.+?)\*\*/);
      const titleText = titleMatch ? titleMatch[1] : '';
      const contentText = bodyText.replace(/^\*\*.+?\*\*\n\n/, '');
      
      setTitle(titleText);
      setContent(contentText);
      setIsPinned(editPost.is_pinned === true);
      
      // Set existing media if available
      if (editPost.file) {
        const isVideo = editPost.file.includes('.mp4') || editPost.file.includes('postVideos');
        setMedia({
          uri: editPost.file,
          type: isVideo ? 'video/mp4' : 'image/jpeg',
          fileName: editPost.file.split('/').pop() || 'media'
        });
      }
    } else if (visible && !isEditMode) {
      // Reset form for new announcement
      resetForm();
    }
  }, [visible, user, communityId, isEditMode, editPost]);

  const checkPermissions = async () => {
    if (!user || !communityId) {
      setCheckingPermissions(false);
      return;
    }
    
    try {
      const result = await canPostAnnouncement(user.id, communityId);
      if (!result.success || !result.data) {
        Alert.alert(
          'Permission Denied',
          'You don\'t have permission to post announcements in this community.',
          [{ text: 'OK', onPress: onClose }]
        );
        setCanPost(false);
      } else {
        setCanPost(true);
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      Alert.alert(
        'Error',
        'Failed to check permissions. Please try again.',
        [{ text: 'OK', onPress: onClose }]
      );
      setCanPost(false);
    } finally {
      setCheckingPermissions(false);
    }
  };

  const handlePickMedia = async () => {
    try {
      // Request media permissions before launching picker
      const permissionResult = await PermissionManager.requestMediaPermissions();
      if (!permissionResult.granted) {
        Alert.alert('Permission Required', 'Gallery access is required to select media files.');
        return;
      }

      const result = await launchImageLibrary({ 
        mediaType: 'mixed',
        quality: 0.7,
        selectionLimit: 1,
      });

      if (result.didCancel) {
        return;
      }
      if (result.errorCode) {
        return;
      }
      if (!result.assets || !result.assets[0]) {
        return;
      }

      const asset = result.assets[0];

      if (!asset.uri) {
        Alert.alert('Error', 'Selected file has no URI');
        return;
      }

      // Validate file size before proceeding
      const sizeValidation = await validateFileSize(asset.uri);
      if (!sizeValidation.isValid) {
        showFileSizeError(sizeValidation.errorMessage || 'File size validation failed');
        return;
      }

      // Show file size info in console
      showFileSizeInfo(sizeValidation.size);

      // Extract extension from MIME type
      let extension = 'jpg';
      let type = asset.type || (asset.type?.startsWith('image') ? 'image/jpeg' : 'video/mp4');
      if (type.startsWith('image/')) {
        extension = type.split('/')[1]; // e.g., 'png', 'jpeg', 'webp'
        if (extension === 'jpeg') extension = 'jpg';
      } else if (type.startsWith('video/')) {
        extension = type.split('/')[1]; // e.g., 'mp4', 'mov'
      } else {
        // Unrecognized file type
      }
      const fileName = asset.fileName || `${asset.type?.startsWith('image') ? 'image' : 'video'}_${Date.now()}.${extension}`;
      
      setMedia({
        uri: asset.uri,
        type,
        fileName,
      });
    } catch (e) {
      // Picker exception
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      Alert.alert('Error', 'Please fill in both title and content');
      return;
    }

    if (!user || !canPost) {
      Alert.alert('Error', 'You don\'t have permission to post announcements');
      return;
    }

    setLoading(true);

    try {
      let mediaPath = null;
      let thumbnailPath: string | null = null;
      
      // Check if media needs to be uploaded (new media or changed media)
      const needsUpload = media && (typeof media === 'object') && !media.uri.startsWith('http');
      
      if (needsUpload && media) {
        setUploading(true);
        const isImage = media.type?.startsWith('image');
        const folder = isImage ? 'postImages' : 'postVideos';

        // If video, generate and upload a thumbnail first
        if (!isImage && media.uri) {
          try {
            const thumbGen = await generateVideoThumbnail(media.uri);
            if (thumbGen.success && thumbGen.path) {
              const thumbUpload = await uploadFile(
                'uploads',
                thumbGen.path,
                true,
                `thumb_${Date.now()}.jpg` as any,
                'postImages'
              );
              if (thumbUpload.success) {
                thumbnailPath = thumbUpload.data;
              }
            }
          } catch (e) {
            // ignore thumbnail errors
          }
        }

        const uploadResult = await uploadFile(
          'uploads',
          media.uri,
          isImage,
          media.fileName as any,
          folder
        );
        setUploading(false);
        
        if (uploadResult.success) {
          mediaPath = uploadResult.data;
        } else {
          Alert.alert('Error', 'Failed to upload media');
          setLoading(false);
          return;
        }
      } else if (media && media.uri.startsWith('http')) {
        // Keep existing media path
        mediaPath = media.uri;
        if (isEditMode && editPost?.thumbnail) {
          thumbnailPath = editPost.thumbnail;
        }
      }

      // Create or update announcement post
      const announcementData: any = {
        body: `**${title}**\n\n${content}`,
        userId: user.id,
        community_id: communityId,
        file: mediaPath,
        is_pinned: isPinned,
        is_announcement: true,
      };

      // If editing, include the post ID
      if (isEditMode && editPost?.id) {
        announcementData.id = editPost.id;
      }

      if (thumbnailPath) {
        announcementData.thumbnail = thumbnailPath;
      }

      const result = isEditMode 
        ? await updateAnnouncement(announcementData)
        : await createAnnouncement(announcementData);

      if (result.success) {
        Alert.alert('Success', isEditMode ? 'Announcement updated successfully!' : 'Announcement posted successfully!');
        resetForm();
        onSuccess();
        onClose();
      } else {
        Alert.alert('Error', result.msg || `Failed to ${isEditMode ? 'update' : 'post'} announcement`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'posting'} announcement:`, error);
      Alert.alert('Error', 'Something went wrong. Please try again.');
    }

    setLoading(false);
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setMedia(null);
    setIsPinned(false);
    setVideoPaused(true);
  };

  const openFullScreenMedia = (mediaType: 'image' | 'video') => {
    setFullScreenMediaType(mediaType);
    setShowFullScreen(true);
  };

  const closeFullScreenMedia = () => {
    setShowFullScreen(false);
  };

  const handleClose = () => {
    if (title.trim() || content.trim() || media) {
      Alert.alert(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Discard',
            style: 'destructive',
            onPress: () => {
              resetForm();
              onClose();
            },
          },
        ]
      );
    } else {
      onClose();
    }
  };

  const isVideo = media?.type?.startsWith('video/');

  // Show loading while checking permissions, or hide modal if permissions denied
  if (checkingPermissions) {
    return (
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={handleClose}
      >
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={{ marginTop: 16, color: theme.colors.textLight }}>Checking permissions...</Text>
        </View>
      </Modal>
    );
  }

  if (!canPost) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.cancelButton}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{isEditMode ? 'Edit Announcement' : 'Post Announcement'}</Text>
          <TouchableOpacity
            onPress={handleSubmit}
            disabled={loading || !title.trim() || !content.trim()}
            style={[
              styles.postButton,
              (!title.trim() || !content.trim()) && styles.postButtonDisabled,
            ]}
          >
            {loading ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Text style={styles.postButtonText}>{isEditMode ? 'Update' : 'Post'}</Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.form}>
            <Text style={styles.label}>Title *</Text>
            <TextInput
              style={styles.titleInput}
              value={title}
              onChangeText={setTitle}
              placeholder="Enter announcement title..."
              placeholderTextColor={theme.colors.textLight}
              maxLength={100}
            />

            <Text style={styles.label}>Content *</Text>
            <TextInput
              style={styles.contentInput}
              value={content}
              onChangeText={setContent}
              placeholder="Enter announcement content..."
              placeholderTextColor={theme.colors.textLight}
              multiline
              textAlignVertical="top"
              maxLength={2000}
            />

            <View style={styles.mediaSection}>
              <Text style={styles.label}>Media (Optional)</Text>
              <TouchableOpacity
                style={styles.mediaPicker}
                onPress={handlePickMedia}
                disabled={uploading}
              >
                {media ? (
                  <View style={styles.mediaPreview}>
                    {isVideo ? (
                      <View style={styles.videoContainer}>
                        <VideoPlayer
                          source={{ uri: media.uri }}
                          style={styles.videoPlayer}
                          controls
                          resizeMode="cover"
                          paused={videoPaused}
                        />
                        <TouchableOpacity
                          onPress={() => setVideoPaused(p => !p)}
                          style={styles.videoControl}
                        >
                          <Text style={styles.videoControlText}>
                            {videoPaused ? '▶️' : '⏸️'}
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => openFullScreenMedia('video')}
                          style={styles.fullScreenButton}
                        >
                          <Text style={styles.fullScreenButtonText}>⛶</Text>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <TouchableOpacity onPress={() => openFullScreenMedia('image')} activeOpacity={0.9}>
                        <Image source={{ uri: media.uri }} style={styles.previewImage} />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      style={styles.removeMedia}
                      onPress={() => setMedia(null)}
                    >
                      <Text style={styles.removeMediaText}>×</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={styles.mediaPlaceholder}>
                    {uploading ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : (
                      <>
                        <View style={styles.mediaIcons}>
                          <ImageIcon size={24} color={theme.colors.dark} />
                          <VideoIcon size={26} color={theme.colors.dark} />
                        </View>
                        <Text style={styles.mediaPlaceholderLabel}>Add Image or Video</Text>
                      </>
                    )}
                  </View>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.pinSection}>
              <Text style={styles.label}>Pin to Top</Text>
              <View style={styles.pinRow}>
                <Text style={styles.pinDescription}>
                  Pinned announcements appear at the top of the community feed
                </Text>
                <Switch
                  value={isPinned}
                  onValueChange={setIsPinned}
                  trackColor={{ false: theme.colors.gray, true: theme.colors.primary }}
                  thumbColor="white"
                />
              </View>
            </View>

            <View style={styles.infoBox}>
              <Text style={styles.infoTitle}>ℹ️ Announcement Guidelines</Text>
              <Text style={styles.infoText}>
                • Keep announcements clear and concise{'\n'}
                • Use for important updates and information{'\n'}
                • Respect community guidelines{'\n'}
                • Only admins and moderators can post announcements{'\n'}
                • Support images and videos for better engagement
              </Text>
            </View>
          </View>
        </ScrollView>

        {/* Full Screen Media Modal */}
        {media && (
          <FullScreenMedia
            visible={showFullScreen}
            onClose={closeFullScreenMedia}
            mediaUrl={media.uri}
            mediaType={fullScreenMediaType}
            title="Announcement Preview"
          />
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'white',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray,
  },
  cancelButton: {
    padding: 8,
  },
  cancelButtonText: {
    color: theme.colors.textLight,
    fontSize: 16,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.dark,
  },
  postButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: theme.radius.sm,
  },
  postButtonDisabled: {
    backgroundColor: theme.colors.gray,
  },
  postButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  form: {
    padding: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 8,
  },
  titleInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray,
    borderRadius: theme.radius.sm,
    padding: 12,
    fontSize: 16,
    marginBottom: 20,
    color: theme.colors.dark,
  },
  contentInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray,
    borderRadius: theme.radius.sm,
    padding: 12,
    fontSize: 16,
    height: 120,
    marginBottom: 20,
    color: theme.colors.dark,
  },
  mediaSection: {
    marginBottom: 20,
  },
  mediaPicker: {
    borderWidth: 2,
    borderColor: theme.colors.gray,
    borderStyle: 'dashed',
    borderRadius: theme.radius.sm,
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mediaPlaceholder: {
    alignItems: 'center',
  },
  mediaIcons: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  mediaPlaceholderLabel: {
    fontSize: 14,
    color: theme.colors.textLight,
  },
  mediaPreview: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    borderRadius: theme.radius.sm,
  },
  videoContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  videoPlayer: {
    width: '100%',
    height: '100%',
    borderRadius: theme.radius.sm,
  },
  videoControl: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoControlText: {
    color: 'white',
    fontSize: 16,
  },
  removeMedia: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 15,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeMediaText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  pinSection: {
    marginBottom: 20,
  },
  pinRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pinDescription: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.textLight,
    marginRight: 16,
  },
  infoBox: {
    backgroundColor: theme.colors.gray,
    padding: 16,
    borderRadius: theme.radius.sm,
    marginTop: 20,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: theme.colors.textLight,
    lineHeight: 20,
  },
  fullScreenButton: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullScreenButtonText: {
    color: 'white',
    fontSize: 20,
  },
});

export default AnnouncementPost; 